import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../components/ui";
import {
  aiContentGetScheduled,
  aiContentPublishNow,
  type AiContentPost,
} from "../../utils/aiContent";
import type { AiContentOutletContext } from "./AiContentLayout";
import { aiActionPrimaryBtn } from "./aiContentUi";

const ScheduledPage = () => {
  const { ownerId, userId, workspaceId } =
    useOutletContext<AiContentOutletContext>();
  const scope = { ownerId, userId, workspaceId };
  const [posts, setPosts] = useState<AiContentPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await aiContentGetScheduled(scope);
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to load scheduled posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ownerId) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, userId, workspaceId]);

  const publishNow = async (post: AiContentPost) => {
    if (
      !window.confirm(
        `Publish day ${post.dayIndex} to LinkedIn now? This posts immediately.`
      )
    ) {
      return;
    }
    setBusyId(post._id);
    try {
      await aiContentPublishNow(scope, post._id);
      toast.success(`Day ${post.dayIndex} published to LinkedIn`);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Publish failed");
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted">Loading scheduled posts…</p>;
  }

  if (!posts.length) {
    return (
      <div className="rounded-[10px] border border-line bg-white px-6 py-10 text-center text-sm text-muted">
        No approved or scheduled posts yet. Approve posts from the 30-Day Plan.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-white">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-line bg-paper text-xs uppercase text-muted">
          <tr>
            <th className="px-4 py-3 font-semibold">Day</th>
            <th className="px-4 py-3 font-semibold">Scheduled</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">Post</th>
            <th className="px-4 py-3 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {posts.map((post) => (
            <tr key={post._id}>
              <td className="px-4 py-3 font-medium text-flow-config-text">
                {post.dayIndex}
              </td>
              <td className="px-4 py-3 text-muted">
                {post.scheduledAt
                  ? new Date(post.scheduledAt).toLocaleString()
                  : "—"}
              </td>
              <td className="px-4 py-3 capitalize text-brand-blue">
                {String(post.status || "").replace(/_/g, " ")}
              </td>
              <td className="max-w-md px-4 py-3 text-flow-config-text">
                <span className="line-clamp-2">{post.postText}</span>
                {post.lastError &&
                post.status !== "published" &&
                post.status !== "skipped" ? (
                  <p className="mt-1 text-xs text-danger">{post.lastError}</p>
                ) : null}
              </td>
              <td className="px-4 py-3">
                {post.status === "scheduled" || post.status === "failed" ? (
                  <Button
                    variant="primary"
                    size="sm"
                    className={aiActionPrimaryBtn}
                    isLoading={busyId === post._id}
                    onClick={() => void publishNow(post)}
                  >
                    Publish now
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ScheduledPage;
